import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { Link } from 'foundations-components/transitional/components/Link';
import { TableHoverCell } from 'foundations-components/transitional/components/Table';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Tag } from 'foundations-components/transitional/components/Tag';
import CloneMeetingsModal from '../clone/CloneMeetingsModalV2';
import MeetingLinkDeleteOrUsagesModal from '../modals/MeetingLinkDeleteOrUsagesModal';
import MeetingsTableActions from './MeetingsTableActions';
import DefaultLinkPopover from '../../components/table/DefaultLinkPopover';
import CopyEmbedScriptPromptV2 from '../../components/embed/CopyEmbedScriptPromptV2';
import { getDefaultMeetingName } from 'meetings-utils/meetingName';
import { getReadableMeetingType } from '../../utils/utils';
import { isAfterCustomAvailabilityPeriod } from 'MeetingsBase/utils/dates';
// @ts-expect-error untyped import
import { getDisplayNameForUser } from '../../selectors/MeetingsUsersSelectors';
import { useCallback, useMemo, useState } from 'react';
import businessUnitsQuery from 'MeetingsLib/query/businessUnitsQuery';
import { useQuery } from 'data-fetching-client';
import { trackUserInteraction } from '../../tracking/UsageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function MeetingsTableRowV2({
  link,
  clickTableAction,
  refetchLinks,
  sendEmbedCodeEmail,
  deleteMeetingsLink,
  clickDefaultLinkTooltip,
  linkStats,
  meetingsUser,
  canEditAllLinks,
  onSelectRow,
  selectedRows,
  linkOwner,
  selectedUserOption,
  updateMeetingsLink,
  canEditMoreThanOneLink,
  hasStarterFeatureAccess,
  isJitaUser,
  shouldRenderBusinessUnit
}) {
  const linkId = link.id;
  const linkName = link.name;
  const linkUrl = link.link;
  const linkUserId = link.userId;
  const [openCloneModal, setOpenCloneModel] = useState(false);
  const [openDeleteOrUsagesModal, setOpenDeleteOrUsagesModal] = useState(false);
  const [openEmbedModal, setOpenEmbedModal] = useState(false);
  const {
    data: businessUnitsData,
    loading: businessUnitLoading,
    error: businessUnitError
  } = useQuery(businessUnitsQuery, {
    skip: !shouldRenderBusinessUnit
  });
  const getLinkName = useCallback(() => {
    return link.name || getDefaultMeetingName(link.customParams.durations, true);
  }, [link.customParams.durations, link.name]);
  const onBulkActionChange = useCallback(() => {
    let newSelectedRows = selectedRows;
    if (selectedRows.has(linkId)) {
      newSelectedRows = selectedRows.delete(linkId);
    } else {
      newSelectedRows = selectedRows.add(linkId);
    }
    onSelectRow(newSelectedRows);
  }, [linkId, onSelectRow, selectedRows]);
  const onDeleteLinkClick = useCallback(() => {
    clickTableAction('delete');
    setOpenDeleteOrUsagesModal(true);
  }, [clickTableAction]);
  const onDeleteLinkConfirm = useCallback(() => {
    deleteMeetingsLink(linkId, linkUserId);
    setOpenDeleteOrUsagesModal(false);
  }, [deleteMeetingsLink, linkId, linkUserId]);
  const onSetLinkAsDefault = useCallback(() => {
    clickTableAction('setLinkAsDefault');
    const updatedLink = link.set('isDefaultLink', true);
    updateMeetingsLink(updatedLink);
  }, [clickTableAction, link, updateMeetingsLink]);
  const onToggleOpenEmbedModal = useCallback(() => {
    clickTableAction('embed');
    setOpenEmbedModal(!openEmbedModal);
  }, [clickTableAction, openEmbedModal]);
  const onEditLinkClick = useCallback(() => {
    if (link.isDefaultLink) {
      trackUserInteraction('default-meeting-link-edit-click');
    }
    clickTableAction('edit');
  }, [clickTableAction, link.isDefaultLink]);
  const onCopyButtonClick = useCallback(() => {
    clickTableAction('copy');
  }, [clickTableAction]);
  const onOpenLinkClick = useCallback(() => {
    clickTableAction('link');
  }, [clickTableAction]);
  const renderedDefaultLinkPopover = useMemo(() => {
    if (!link.isDefaultLink) {
      return null;
    }
    return /*#__PURE__*/_jsx(DefaultLinkPopover, {
      clickDefaultLinkTooltip: clickDefaultLinkTooltip
    });
  }, [clickDefaultLinkTooltip, link.isDefaultLink]);
  const renderedCustomAvailabilityError = useMemo(() => {
    const endDate = link.customParams.customAvailabilityEndDate;
    const todayUTC = I18n.moment.utc().startOf('day');
    if (!endDate || endDate && !isAfterCustomAvailabilityPeriod(todayUTC, I18n.moment(endDate))) {
      return null;
    }
    return /*#__PURE__*/_jsx(Tooltip, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "adminHeader.customAvailabilityTooltip.tooltip"
      }),
      children: /*#__PURE__*/_jsx(Text, {
        as: "small",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "adminHeader.customAvailabilityTooltip.text"
        })
      })
    });
  }, [link.customParams.customAvailabilityEndDate]);
  const renderedLink = useMemo(() => {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        children: [/*#__PURE__*/_jsx("strong", {
          "data-selenium-test": "meeting-link-name",
          children: getLinkName()
        }), link.paymentLinkId && /*#__PURE__*/_jsx(Tag, {
          inline: true,
          children: I18n.text('paymentText')
        })]
      }), /*#__PURE__*/_jsx(Text, {
        as: "small",
        "data-onboarding": "meeting-link-name",
        children: /*#__PURE__*/_jsx(Link, {
          href: linkUrl,
          "data-selenium-test": "public-booking-link",
          external: true,
          onClick: onOpenLinkClick,
          children: link.slug
        })
      }), renderedDefaultLinkPopover, /*#__PURE__*/_jsx("br", {}), renderedCustomAvailabilityError]
    });
  }, [getLinkName, link.paymentLinkId, link.slug, linkUrl, onOpenLinkClick, renderedCustomAvailabilityError, renderedDefaultLinkPopover]);
  const handleCloneModalClick = useCallback(() => {
    if (openCloneModal) refetchLinks();
    setOpenCloneModel(!openCloneModal);
  }, [openCloneModal, refetchLinks]);
  const renderedActions = useMemo(() => {
    return /*#__PURE__*/_jsx(MeetingsTableActions, {
      link: link,
      meetingsUser: meetingsUser,
      handleSetAsDefaultButtonClick: onSetLinkAsDefault,
      handleEditLinkClick: onEditLinkClick,
      handleDeleteLinkClick: onDeleteLinkClick,
      handleOpenEmbedModal: onToggleOpenEmbedModal,
      handleCopyButtonClick: onCopyButtonClick,
      selectedUserOption: selectedUserOption,
      canEditAllLinks: canEditAllLinks,
      canEditMoreThanOneLink: canEditMoreThanOneLink,
      handleOpenCloneModal: handleCloneModalClick,
      hasStarterFeatureAccess: hasStarterFeatureAccess,
      isJitaUser: isJitaUser
    });
  }, [link, meetingsUser, onSetLinkAsDefault, onEditLinkClick, onDeleteLinkClick, onToggleOpenEmbedModal, onCopyButtonClick, selectedUserOption, canEditAllLinks, canEditMoreThanOneLink, handleCloneModalClick, hasStarterFeatureAccess, isJitaUser]);
  const renderedBulkActions = useMemo(() => {
    return /*#__PURE__*/_jsx(Checkbox, {
      "data-selenium-test": "bulk-action-checkbox",
      "aria-label": I18n.text('adminHeader.table.bulkActions.actionLabel'),
      checked: selectedRows.has(linkId),
      onChange: onBulkActionChange
    });
  }, [linkId, onBulkActionChange, selectedRows]);
  const renderedDuration = useMemo(() => {
    if (link.get('customParams').get('durations').size === 1) {
      return getDefaultMeetingName(link.customParams.durations);
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Text, {
        as: "small",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "adminHeader.multipleDurations"
        })
      }), /*#__PURE__*/_jsx(HelpIcon, {
        alignWithText: true,
        marginInlineStart: "100",
        size: "100",
        title: getDefaultMeetingName(link.customParams.durations)
      })]
    });
  }, [link]);
  const renderedBusinessUnit = useMemo(() => {
    if (!shouldRenderBusinessUnit || businessUnitLoading || businessUnitError || !businessUnitsData) {
      return null;
    }
    const currentBusinessUnit = businessUnitsData.businessUnits.find(businessUnit => businessUnit.id === link.businessUnitId);
    return currentBusinessUnit && currentBusinessUnit.name || null;
  }, [businessUnitError, businessUnitLoading, businessUnitsData, link.businessUnitId, shouldRenderBusinessUnit]);
  const renderedStats = useMemo(() => {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("td", {
        children: linkStats.viewCount
      }, `${link.name}-views`), /*#__PURE__*/_jsx("td", {
        children: linkStats.bookCount
      }, `${link.name}-bookings`), /*#__PURE__*/_jsx("td", {
        children: linkStats.conversionRate
      }, `${link.name}-conversions`)]
    });
  }, [linkStats.bookCount, linkStats.conversionRate, linkStats.viewCount, link.name]);
  const renderedLinkOwner = useMemo(() => {
    if (!linkOwner) {
      return null;
    }
    return getDisplayNameForUser(linkOwner);
  }, [linkOwner]);
  const renderedDeleteOrUsagesModal = useMemo(() => {
    if (!openDeleteOrUsagesModal) return null;
    return /*#__PURE__*/_jsx(MeetingLinkDeleteOrUsagesModal, {
      linkId: linkId,
      linkName: linkName,
      handleConfirm: onDeleteLinkConfirm,
      handleClose: () => setOpenDeleteOrUsagesModal(false)
    });
  }, [linkId, linkName, onDeleteLinkConfirm, openDeleteOrUsagesModal]);
  const renderedRow = useMemo(() => {
    return /*#__PURE__*/_jsxs("tr", {
      "data-selenium-test": `meetings-table-row-${link.slug ? link.slug : ''}`,
      "data-test-id": `meetings-table-row-${link.slug ? link.slug : ''}`,
      "data-onboarding": `meetings-table-row${link.isDefaultLink ? '-default' : ''}`,
      children: [canEditAllLinks && /*#__PURE__*/_jsx("td", {
        children: renderedBulkActions
      }, `${link.name}-bulk-actions`), /*#__PURE__*/_jsx(TableHoverCell, {
        hoverContent: /*#__PURE__*/_jsx("div", {
          children: renderedActions
        }, `${link.name}-actions`),
        children: renderedLink
      }), /*#__PURE__*/_jsx("td", {
        children: renderedLinkOwner
      }, `${link.name}-owner`), /*#__PURE__*/_jsx("td", {
        children: getReadableMeetingType(link.type)
      }, `${link.name}-type`), /*#__PURE__*/_jsx("td", {
        children: renderedDuration
      }, `${link.name}-durations`), shouldRenderBusinessUnit && /*#__PURE__*/_jsx("td", {
        children: renderedBusinessUnit
      }, `${link.name}-businessUnit`), renderedStats, openCloneModal && /*#__PURE__*/_jsx(CloneMeetingsModal, {
        link: link,
        handleCloseModal: handleCloneModalClick
      }), openEmbedModal && /*#__PURE__*/_jsx(CopyEmbedScriptPromptV2, {
        link: link,
        sendEmbedCodeEmail: sendEmbedCodeEmail,
        onClose: onToggleOpenEmbedModal
      }), renderedDeleteOrUsagesModal]
    });
  }, [link, canEditAllLinks, renderedBulkActions, renderedActions, renderedLink, renderedLinkOwner, renderedDuration, shouldRenderBusinessUnit, renderedBusinessUnit, renderedStats, openCloneModal, handleCloneModalClick, openEmbedModal, renderedDeleteOrUsagesModal, sendEmbedCodeEmail, onToggleOpenEmbedModal]);
  return renderedRow;
}
export default MeetingsTableRowV2;